import { useState, useEffect, Suspense } from 'react';
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import axios from 'axios';
import BuildingsCatalogSkeletonItem from './BuildingsCatalogSkeletonItem';
import BuildingsCatalogSort from './BuildingsCatalogSort';
import { useLocationState } from '../../context/location';
import { useBuildingsProvider } from '../../context/buildings';
import Skelet from '../Skelet';
import { declOfNum, sleep } from '../../helpers';
import { useIsMount } from '../../hooks/useIsMount';
import styles from '../../styles/modules/buildings/BuildingsCatalog.module.scss';

const generatePagination = (current, last) => {
  const delta = 1;
  const left = current - delta;
  const right = current + delta + 1;
  const range = [];
  const rangeWithDots = [];

  let l;

  for (let i = 1; i <= last; i += 1) {
    if (i === 1 || i === last || (i >= left && i < right)) {
      range.push(i);
    }
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push(null);
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

const BuildingsCatalogItem = dynamic(() => import('./BuildingsCatalogItem'), { suspense: true });

const BuildingsCatalog = () => {
  const isMount = useIsMount();
  const locationState = useLocationState();
  const [buildingsState, setBuildingsState] = useBuildingsProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [totalFlats, setTotalFlats] = useState(null);
  const [totalBuildings, setTotalBuildings] = useState(null);
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await sleep(300);

        const response = await axios.post(
          process.env.NEXT_PUBLIC_API + '/buildings',
          {
            ...buildingsState.filter,
            ...buildingsState.pagination,
            ...buildingsState.sorting,
            location: locationState.name
          },
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setTotalBuildings(response.data.totalBuildings);
        setTotalFlats(response.data.totalFlats);
        setPage(response.data.data.current_page);
        setPages(response.data.data.last_page);
        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [buildingsState.filter, locationState]);
  useEffect(() => {
    if (isMount || isLoading) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        await sleep(300);

        const response = await axios.post(
          process.env.NEXT_PUBLIC_API + '/buildings',
          {
            ...buildingsState.filter,
            ...buildingsState.pagination,
            ...buildingsState.sorting,
            location: locationState.name
          },
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setPage(response.data.data.current_page);
        setData(response.data.data.data);
        setIsLoadingData(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoadingData(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [buildingsState.pagination]);
  useEffect(() => window.scrollTo({ top: 0, behavior: 'smooth' }), [locationState]);

  const handlePagingClick = (value, modificator = 0) => {
    if (value + modificator === page || value + modificator <= 0 || value + modificator > pages)
      return;

    setPage(value + modificator);
    setBuildingsState({ type: 'SET_PAGINATION', payload: { page: value + modificator } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const isSortSelected = (sort, dir) => {
    if (!sort) return false;

    if (buildingsState.sorting.sort === sort) {
      if (dir) {
        if (buildingsState.sorting.sortDirection === dir) return true;

        return false;
      }

      return true;
    }

    return false;
  };

  if (isLoading || isError) {
    return (
      <section className={['block', styles.clear].join(' ')}>
        <div className={['fixed', styles.catalog].join(' ')}>
          <div className={styles.catalogHeader}>
            <Skelet height={32} maxWidth={400} className={styles.catalogHeaderTitle} />
            <Skelet height={32} maxWidth={200} className={styles.catalogHeaderActions} />
          </div>
          <div className={styles.catalogContent}>
            {[...Array(5).keys()].map((value) => (
              <BuildingsCatalogSkeletonItem key={value} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={['block', styles.clear].join(' ')}>
      <div className={['fixed', styles.catalog].join(' ')}>
        {!!data.length && (
          <div className={styles.catalogHeader}>
            <div className={styles.catalogHeaderTitle}>
              {declOfNum(totalFlats, ['Найдена', 'Найдено', 'Найдено'])} {totalFlats}{' '}
              {declOfNum(totalFlats, ['квартира', 'квартиры', 'квартир'])}{' '}
              <span>в {totalBuildings} ЖК</span>
            </div>
            <div className={styles.catalogHeaderActions}>
              <BuildingsCatalogSort
                content={[
                  {
                    text: 'По сроку сдачи',
                    sort: 'built-year.max',
                    selected: isSortSelected('built-year.max')
                  },
                  {
                    text: 'Цена по возрастанию',
                    sort: 'price.value',
                    direction: 'asc',
                    selected: isSortSelected('price.value', 'asc')
                  },
                  {
                    text: 'Цена по убыванию',
                    sort: 'price.value',
                    direction: 'desc',
                    selected: isSortSelected('price.value', 'desc')
                  },
                  {
                    text: 'Цена по возрастанию за м²',
                    sort: 'priceM2',
                    direction: 'asc',
                    selected: isSortSelected('priceM2', 'asc')
                  },
                  {
                    text: 'Цена по убыванию за м²',
                    sort: 'priceM2',
                    direction: 'desc',
                    selected: isSortSelected('priceM2', 'desc')
                  }
                ]}
              />
            </div>
          </div>
        )}
        <div className={styles.catalogContent}>
          {isLoadingData ? (
            [...Array(3).keys()].map((value) => <BuildingsCatalogSkeletonItem key={value} />)
          ) : !!data.length ? (
            data.map((value, index) => (
              <Suspense fallback={<BuildingsCatalogSkeletonItem />} key={index}>
                <BuildingsCatalogItem item={value} />
              </Suspense>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: { duration: 1, type: 'spring', bounce: 0.6 }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <h5 style={{ marginBottom: 8 }}>Нет вариантов в {locationState.case}</h5>
              <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                Попробуйте изменить фильтры или расширить область поиска
              </div>
              <img
                src="/images/wondering-man.svg"
                alt=""
                style={{ width: '100%', maxWidth: 400, margin: '18px 32px' }}
              />
            </motion.div>
          )}
        </div>
        {pages > 1 && (
          <div className={styles.catalogFooter}>
            <button
              className={styles.catalogFooterLeft}
              type="button"
              aria-label="Предыдущая страница"
              onClick={() => handlePagingClick(page, -1)}
              style={{
                cursor: page <= 1 ? 'not-allowed' : null,
                opacity: page <= 1 ? 0.1 : null
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {generatePagination(page, pages).map((value, index) => {
              if (!value) return <span key={index}>...</span>;

              return (
                <button
                  className={[styles.catalogFooterButton, value === page ? styles.active : ''].join(
                    ' '
                  )}
                  type="button"
                  key={index}
                  onClick={() => handlePagingClick(value)}
                  style={{ cursor: value === page ? 'default' : null }}
                >
                  {value}
                </button>
              );
            })}
            <button
              className={styles.catalogFooterRight}
              type="button"
              aria-label="Следующая страница"
              onClick={() => handlePagingClick(page, 1)}
              style={{
                cursor: page >= pages ? 'not-allowed' : null,
                opacity: page >= pages ? 0.1 : null
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default BuildingsCatalog;
